import { api } from '../api';

const GETTING_STARTED_BASE = '/developer/getting-started';

const gettingStartedApi = {
  /**
   * Get user's getting started progress
   * @param {string} userId - User ID
   * @returns {Promise<{data: Object|null, error: string|null}>} Progress data
   */
  async getUserProgress(userId) {
    try {
      const response = await api.get(`${GETTING_STARTED_BASE}/progress/${userId}`);
      // Transform the response to match frontend expectations
      const transformedData = {
        ...response,
        data: {
          ...response.data,
          // Map backend snake_case to frontend camelCase
          totalSteps: response.data.total_steps,
          completedSteps: response.data.completed_steps,
          steps: response.data.steps.map(step => ({
            ...step,
            completedAt: step.completed_at
          }))
        }
      };
      return { data: transformedData, error: null };
    } catch (error) {
      console.error('Error getting user progress:', error);
      return { 
        data: null, 
        error: error.message || 'Failed to fetch progress' 
      };
    }
  },

  /**
   * Mark a step as complete
   * @param {string} userId - User ID
   * @param {number} stepId - Step ID to mark as complete
   * @returns {Promise<{data: Object|null, error: string|null}>} Operation result
   */
  async markStepComplete(userId, stepId) {
    try {
      const response = await api.put(
        `${GETTING_STARTED_BASE}/progress/${userId}/complete`,
        { stepId }
      );
      
      return { 
        data: response, 
        error: null 
      };
    } catch (error) {
      console.error('Error marking step as complete:', error);
      return { 
        data: null, 
        error: error.message || 'Failed to mark step as complete' 
      };
    }
  },

  /**
   * Reset a step to incomplete
   * @param {string} userId - User ID
   * @param {number} stepId - Step ID to reset
   * @returns {Promise<Object>} Operation result
   */
  async resetStep(userId, stepId) {
    try {
      const response = await api.post(
        `${GETTING_STARTED_BASE}/progress/${userId}/reset`,
        { stepId }
      );
      return {
        success: true,
        data: response
      };
    } catch (error) {
      console.error('Error resetting step:', error);
      return {
        success: false,
        error: error.message || 'Failed to reset step'
      };
    }
  },

  /**
   * Initialize getting started steps for a new user
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Operation result
   */
  async initializeUserSteps(userId) {
    try {
      const response = await api.post(
        `${GETTING_STARTED_BASE}/initialize`,
        { userId }
      );
      return {
        success: true,
        data: response
      };
    } catch (error) {
      console.error('Error initializing user steps:', error);
      return {
        success: false,
        error: error.message || 'Failed to initialize user steps'
      };
    }
  },

  /**
   * Get getting started progress summary
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Summary data
   */
  async getProgressSummary(userId) {
    try {
      const response = await api.get(`${GETTING_STARTED_BASE}/summary/${userId}`);
      return {
        success: true,
        data: response
      };
    } catch (error) {
      console.error('Error getting progress summary:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch progress summary'
      };
    }
  },

  /**
   * Reset all getting started progress (except account creation)
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Operation result
   */
  async resetAllProgress(userId) {
    try {
      const response = await api.post(
        `${GETTING_STARTED_BASE}/reset/${userId}`,
        {}
      );
      return {
        success: true,
        data: response
      };
    } catch (error) {
      console.error('Error resetting all progress:', error);
      return {
        success: false,
        error: error.message || 'Failed to reset all progress'
      };
    }
  },

  /**
   * Check if a specific step is completed
   * @param {string} userId - User ID
   * @param {number} stepId - Step ID to check
   * @returns {Promise<Object>} Check result
   */
  async isStepCompleted(userId, stepId) {
    try {
      const response = await api.get(
        `${GETTING_STARTED_BASE}/check-step/${userId}/${stepId}`
      );
      return {
        success: true,
        data: response
      };
    } catch (error) {
      console.error('Error checking step completion:', error);
      return {
        success: false,
        error: error.message || 'Failed to check step completion'
      };
    }
  },

  /**
   * Get the next incomplete step
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Next step data
   */
  async getNextStep(userId) {
    try {
      const response = await api.get(
        `${GETTING_STARTED_BASE}/next-step/${userId}`
      );
      return {
        success: true,
        data: response
      };
    } catch (error) {
      console.error('Error getting next step:', error);
      return {
        success: false,
        error: error.message || 'Failed to get next step'
      };
    }
  }
};

export default gettingStartedApi;
